import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Wand2,
  Upload,
  Image,
  RefreshCw,
  X,
  Download,
  Send,
  Sparkles,
  AlertTriangle,
  Plus,
  Edit3,
  ArrowLeft,
  ArrowRight,
  Tag,
  Check
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { supabase } from '../supabaseClient';
import CatalogProductPicker from './CatalogProductPicker';

/**
 * FlyerBuilder - AI-Powered Flyer Generator (Multi-Image)
 *
 * Variants:
 *   - "default" — original card-chromed look (used in drawers / smaller surfaces)
 *   - "hero"    — premium hero presentation; no internal card chrome, larger prompt,
 *                  parent provides the title and surrounding scaffolding.
 *
 * Flow:
 * 1. User describes the flyer they want
 * 2. Optionally picks products from the catalog (by name or item number)
 *    and/or uploads product images — up to 4 images in total. For catalog
 *    picks the backend reads the photo and the product facts from the
 *    database; only { sku, brand } is sent.
 * 3. AI (Gemini 2.5 Flash) generates HTML/CSS flyer
 * 4. User previews and can regenerate
 * 5. On approve: captures to PNG, uploads to Supabase Storage
 * 6. Returns the image URL for MMS sending
 */
const DEFAULT_FORMAT = {
  id: 'flyer',
  label: 'Flyer',
  width: 600,
  height: null,        // null = variable height (legacy flyer behavior)
  aspectRatio: null,
  platform: 'general',
  deliveryMode: 'sms', // 'sms' = upload + send via MMS; 'social' = direct download
};

// The brief sent when the user picked catalog products but typed nothing:
// the products themselves are the ask. Typed text always wins.
const briefFor = (text, products) => {
  const typed = (text || '').trim();
  if (typed) return typed;
  if (!products || products.length === 0) return '';
  return `Feature ${products.map((p) => p.name || p.sku).join('; ')}`.slice(0, 2000);
};

// AI design style presets — each maps to a font pairing + palette + layout
// approach in the backend prompt. Keep IDs aligned with STYLE_BLOCKS in
// generate-flyer.py. The `hint` is the tiny tooltip-y caption under each pill.
const STYLES = [
  { id: 'editorial',  label: 'Editorial',  hint: 'Italic serif, magazine' },
  { id: 'modern',     label: 'Modern',     hint: 'Sans-only, geometric' },
  { id: 'classic',    label: 'Classic',    hint: 'Traditional, symmetric' },
  { id: 'minimalist', label: 'Minimalist', hint: 'Sparse, restrained' },
  { id: 'bold',       label: 'Bold',       hint: 'Heavy, high impact' },
];

// ==========================================
// SCALE TO FIT — measures available space and scales a fixed-size child down
// (never up past 1) so it always fits without clipping or distorting. The
// child keeps its exact pixel dimensions and aspect ratio; only a CSS
// transform shrinks it. This is how design tools keep a device mockup the
// right shape while fitting whatever space is available on any screen.
//   baseWidth / baseHeight — the child's natural size (e.g. a 300×620 phone)
// The wrapper reserves the scaled footprint so surrounding layout stays tight.
// ==========================================
const ScaleToFit = ({ baseWidth, baseHeight, children, className = '' }) => {
  const boxRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      // Fit within the box on both axes; may enlarge up to 1.75x so the
      // workspace mockup reads big on desktop (75% larger than natural).
      const next = Math.min(width / baseWidth, height / baseHeight, 1.75);
      setScale(next > 0 ? next : 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [baseWidth, baseHeight]);

  return (
    <div ref={boxRef} className={`w-full h-full flex items-center justify-center ${className}`}>
      <div style={{ width: baseWidth * scale, height: baseHeight * scale }}>
        <div
          style={{
            width: baseWidth,
            height: baseHeight,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// PHONE MOCKUP FRAME — wraps the live preview in a modern grey iPhone-style
// device. Used by the split variant so the user sees their design in context
// (Stories full-bleed / IG feed / Messages MMS) as it generates.
//
// The frame renders at a fixed natural size (no aspect-ratio/flex squish);
// ScaleToFit shrinks it to fit the available space when needed.
// ==========================================
const PhoneFrameSplit = ({ children, screenRef, width, height }) => (
  <div
    className="relative bg-gradient-to-br from-slate-500 to-slate-700 rounded-[2.5rem] p-2 shadow-2xl ring-1 ring-slate-400/40"
    style={{
      // A phone is always the same physical shape regardless of the design
      // format shown inside it. Fixed size avoids flex/aspect-ratio squish.
      // Explicit width/height (mobile branch) override this.
      ...(width || height
        ? { width, height }
        : { width: 300, height: 620 }),
    }}
  >
    {/* Notch */}
    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-28 h-6 bg-slate-700 rounded-b-2xl z-20 flex items-center justify-center gap-2">
      <div className="w-1.5 h-1.5 rounded-full bg-slate-500" />
      <div className="w-8 h-1.5 rounded-full bg-slate-500" />
    </div>
    {/* Screen */}
    <div ref={screenRef} className="relative w-full h-full bg-white rounded-[2rem] overflow-hidden flex flex-col">
      {children}
    </div>
  </div>
);

const PhoneStatusBar = ({ tone = 'dark' }) => (
  <div className={`flex-shrink-0 h-6 flex items-center justify-between px-4 pt-1 text-[9px] font-bold tabular-nums z-10 ${tone === 'light' ? 'text-white' : 'text-slate-900'}`}>
    <span>9:41</span>
    <div className={`w-3 h-1.5 rounded-[2px] border ${tone === 'light' ? 'border-white/90' : 'border-slate-900'}`}>
      <div className={`h-full w-2/3 ${tone === 'light' ? 'bg-white/90' : 'bg-slate-900'} rounded-[1px]`} />
    </div>
  </div>
);

const FlyerBuilder = ({
  storeId,
  businessName,
  businessInfo = '',
  onFlyerReady,
  onCancel,
  addNotification,
  variant = 'default',
  format = DEFAULT_FORMAT,
  // When a saved design is clicked from the Recent Designs strip, the parent
  // mounts FlyerBuilder with this prop populated. We restore generation state
  // (HTML, prompt, style, image previews) so the user lands directly on the
  // refine/download view instead of a blank prompt.
  initialDesign = null,
  // When the parent's chat-first composer drives entry (user typed a prompt on
  // the Studio home and hit Generate), the typed text arrives here. We seed
  // `description` and fire a SINGLE generation on mount so the user lands
  // straight on the generating → refine view. One-shot — guarded by a ref.
  initialPrompt = '',
  initialImages = null,
  // Catalog products picked in the Studio home composer: [{ sku, name, brand, image }].
  initialProducts = null,
  onPromptConsumed,
  // Optional node rendered at the very top of the left controls column (split
  // variant only). The Studio passes its format breadcrumb here so the
  // breadcrumb consumes left-column space instead of pushing the whole body —
  // and the right workspace card stays aligned with the picker's preview card.
  leftHeader = null,
}) => {
  const [description, setDescription] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  // Products picked from the catalog: [{ sku, name, brand, image }]. The
  // backend looks each one up by sku + brand and designs from that record.
  const [selectedProducts, setSelectedProducts] = useState([]);
  // Every image URL the last generation was built with (catalog photos
  // included), as reported by the backend — saved with the design.
  const lastImageUrlsRef = useRef([]);
  const [generatedHtml, setGeneratedHtml] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState('');
  const [editNote, setEditNote] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('editorial');
  // Current Deal flow — explicit save state so the button can show
  // saving/saved feedback (auto-save runs in the background regardless).
  const [isSavingDeal, setIsSavingDeal] = useState(false);
  const [dealSaved, setDealSaved] = useState(false);
  // Preview view-mode toggle — the user can flip between "Design" (just the
  // flyer alone, larger, no chrome) and "Mockup" (design wrapped in a phone
  // wireframe so they see how it'll feel in Messages / IG / Stories).
  // Defaults to design so the first look after generation is the full design
  // itself; the in-phone mockup is one tap away.
  const [viewMode, setViewMode] = useState('design'); // 'design' | 'mockup'

  // Mobile-viewport detector for the split variant. Below the Tailwind `md:`
  // breakpoint (768px) we render a Canva-style single-column editor instead
  // of the desktop two-pane split — the phone-mockup framing wastes too much
  // real estate on a real phone (the phone is already a phone).
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia('(max-width: 767.98px)');
    const handler = (e) => setIsMobileViewport(e.matches);
    // Sync once in case the initial state was set before mount completed
    setIsMobileViewport(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler); // Safari < 14 fallback
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);

  const fileInputRef = useRef(null);
  const flyerPreviewRef = useRef(null);
  const previewContainerRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.3);
  const [hasMeasuredScale, setHasMeasuredScale] = useState(false);
  const [flyerHeight, setFlyerHeight] = useState(800);

  // Canvas dimensions derived from format. Height is null for variable-height
  // formats like flyers; fixed for social formats (Instagram post, Story, etc).
  const FORMAT_WIDTH = format?.width || 600;
  const FORMAT_HEIGHT = format?.height || null;
  const HAS_FIXED_HEIGHT = Boolean(FORMAT_HEIGHT);
  const IS_SOCIAL = format?.deliveryMode === 'social';
  const FORMAT_LABEL = format?.label || 'Flyer';
  const FORMAT_PLATFORM = format?.platform || 'general';
  // "Current Deal" template: auto-publishes to the phone agent's FAQ via the
  // is_current_deal flag on marketing_designs (set by /api/designs based on
  // format_id). The primary CTA changes from "Send via SMS" to "Save deal"
  // because the agent — not the user — sends these to callers on demand.
  const IS_CURRENT_DEAL = Boolean(format?.isCurrentDeal);

  // Reset generated content when format changes — old HTML won't match new dims
  useEffect(() => {
    setGeneratedHtml('');
    setEditNote('');
    setDealSaved(false);
    setViewMode('design');
    setHasMeasuredScale(false);
  }, [format?.id]);

  // ============================================================
  // RESTORE FROM SAVED DESIGN — when the parent mounts us with an
  // initialDesign (user clicked a card in the Recent Designs strip),
  // populate state so they land directly on the refine view.
  // ============================================================
  useEffect(() => {
    if (!initialDesign) return;
    setDescription(initialDesign.description || '');
    setSelectedStyle(initialDesign.style || 'editorial');
    setGeneratedHtml(initialDesign.html || '');
    setEditNote('');
    setError('');
    // A saved design keeps its catalog photos as plain image URLs (below);
    // the picks themselves aren't stored with it.
    setSelectedProducts([]);
    lastImageUrlsRef.current = [];

    // Image previews — image_urls were uploaded URLs from the previous gen.
    // We rehydrate them as preview-only entries so the strip shows what was
    // attached. They keep the existing url so a regenerate works without
    // re-uploading.
    const urls = Array.isArray(initialDesign.image_urls) ? initialDesign.image_urls : [];
    if (urls.length > 0) {
      setUploadedImages(urls.map((u) => ({
        file: null,
        preview: u,
        url: u,
        uploaded: true,
      })));
    } else {
      setUploadedImages([]);
    }
    // We intentionally only run on initial mount / when a fresh design comes in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDesign?.id]);

  // ============================================================
  // AUTO-GENERATE FROM CHAT-FIRST COMPOSER — when the parent's Studio
  // home composer hands us a typed prompt, seed `description` and fire a
  // single generation. Guarded by a ref so refines/re-renders never
  // re-trigger it. We skip entirely if a saved design was restored
  // (initialDesign wins — that path already has HTML).
  // ============================================================
  const autoGenFiredRef = useRef(false);
  useEffect(() => {
    if (autoGenFiredRef.current) return;
    if (initialDesign) { autoGenFiredRef.current = true; return; }
    const seed = (initialPrompt || '').trim();
    const seededProducts = Array.isArray(initialProducts) ? initialProducts.slice(0, 4) : [];
    // Picked products are a complete brief on their own — no prompt needed.
    if (!seed && seededProducts.length === 0) return;

    autoGenFiredRef.current = true;
    // Seed visible state so the prompt/images show in the editor UI...
    setDescription(seed);
    const seededImages = Array.isArray(initialImages) ? initialImages.slice(0, 4 - seededProducts.length) : [];
    if (seededImages.length > 0) setUploadedImages(seededImages);
    if (seededProducts.length > 0) setSelectedProducts(seededProducts);
    onPromptConsumed?.();
    // ...but generate from explicit values so we don't wait on a state commit.
    // This fires isGenerating synchronously, so the phone mockup lands already
    // showing the "Designing…" animation — no blank-then-jump.
    handleGenerate({ description: seed, images: seededImages, products: seededProducts });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, initialProducts, initialDesign]);

  // ============================================================
  // AUTO-SAVE — fire-and-forget after every successful generation.
  // Captures a PNG thumbnail, uploads it to Supabase Storage, and
  // POSTs to /api/designs. Failures are logged but don't surface
  // to the user (it's a background convenience, not a primary action).
  // We track the last-saved HTML so re-renders / refines don't double-save.
  //
  // Debounced by 1.5s — gives the visible preview time to settle and
  // images time to load before captureFlyer() rasterises. Without this,
  // html2canvas race-conditions against still-loading <img> elements and
  // throws "image argument is a canvas element with a width or height of 0".
  // ============================================================
  const lastSavedHtmlRef = useRef('');
  useEffect(() => {
    if (!generatedHtml || isGenerating) return;
    if (lastSavedHtmlRef.current === generatedHtml) return;
    if (!storeId) return;

    // Don't re-save when we just restored a saved design — the HTML was
    // already in the table, that's where we got it.
    if (initialDesign && initialDesign.html === generatedHtml) {
      lastSavedHtmlRef.current = generatedHtml;
      return;
    }

    // Mark this HTML as "intent to save" immediately so a second effect
    // run during the debounce window doesn't queue another save.
    lastSavedHtmlRef.current = generatedHtml;

    const t = setTimeout(() => {
      // If the user kicked off an explicit Send/Download in the meantime,
      // skip the background save — captureFlyer is already running and
      // running it twice from different code paths can race.
      if (isCapturing) return;
      autoSaveDesign().catch((e) => {
        // Silent — log but never break the UI for an auto-save failure
        console.warn('Auto-save failed:', e?.message || e);
      });
    }, 1500);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedHtml, isGenerating]);

  const autoSaveDesign = async () => {
    // Capture canvas → upload PNG → POST design row
    let thumbnailUrl = null;
    try {
      const canvas = await captureFlyer();
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.85));
      if (blob) {
        const filename = `${storeId}/${Date.now()}_${(format?.id || 'flyer')}.png`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('marketing-designs')
          .upload(filename, blob, {
            contentType: 'image/png',
            cacheControl: '604800',  // 7 days
            upsert: false,
          });
        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from('marketing-designs')
            .getPublicUrl(filename);
          thumbnailUrl = urlData?.publicUrl || null;
        } else {
          console.warn('Thumbnail upload skipped:', uploadError.message);
        }
      }
    } catch (e) {
      console.warn('Thumbnail capture skipped:', e?.message || e);
    }

    // POST the design row regardless of thumbnail success — the HTML is the
    // primary asset, the thumbnail is a convenience.
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return;

    // Prefer what the backend says the design was built with — that list
    // includes the catalog photos, which never pass through uploadedImages.
    const imageUrls = lastImageUrlsRef.current.length > 0
      ? lastImageUrlsRef.current
      : uploadedImages.map((img) => img.url).filter(Boolean);

    const payload = {
      store_id: storeId,
      format_id: format?.id || 'flyer',
      format_label: FORMAT_LABEL,
      format_width: FORMAT_WIDTH,
      format_height: FORMAT_HEIGHT,
      format_aspect_ratio: format?.aspectRatio || null,
      platform: FORMAT_PLATFORM,
      delivery_mode: format?.deliveryMode || 'sms',
      style: selectedStyle,
      description: briefFor(description, selectedProducts),
      business_name: businessName,
      business_info: businessInfo || null,
      image_urls: imageUrls,
      html: generatedHtml,
      thumbnail_url: thumbnailUrl,
      // Current-deal flag — when this format is the "Current Deal" template,
      // /api/designs writes is_current_deal=true on the row, which makes it
      // surface in the phone agent's FAQ "Current Deals" tab and get
      // prioritized by send_flyer when callers ask about sales/specials.
      is_current_deal: Boolean(format?.isCurrentDeal),
    };

    const resp = await fetch('/api/designs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(`POST /api/designs ${resp.status}: ${txt.slice(0, 200)}`);
    }
    console.log('💾 Design auto-saved');
  };

  // Explicit save for the Current Deal flow. autoSaveDesign already runs in
  // the background on every regenerate, but the user wants an explicit
  // "this is my deal" action with confirmation. We just wrap autoSaveDesign
  // with state for button feedback.
  const handleSaveDeal = async () => {
    setIsSavingDeal(true);
    setError('');
    try {
      await autoSaveDesign();
      setDealSaved(true);
      // Reset the affordance after a few seconds so the button returns to
      // an actionable state — they may want to refine and save again.
      setTimeout(() => setDealSaved(false), 3000);
    } catch (e) {
      console.error('Save deal failed:', e);
      setError(e?.message || 'Could not save deal — try again.');
    } finally {
      setIsSavingDeal(false);
    }
  };

  // Legacy alias for any internal references that still use FLYER_WIDTH
  const FLYER_WIDTH = FORMAT_WIDTH;
  const MAX_IMAGES = 4;
  const isHero = variant === 'hero';
  const isSplit = variant === 'split';
  // A design takes MAX_IMAGES images in total; each catalog pick uses one.
  const imageSlotsLeft = MAX_IMAGES - uploadedImages.length - selectedProducts.length;
  // Picking a product is enough to generate — the catalog facts are the brief.
  const canGenerate = Boolean(description.trim()) || selectedProducts.length > 0;

  // Re-measure flyer height after HTML is injected and rendered
  useEffect(() => {
    if (!generatedHtml || !flyerPreviewRef.current) return;
    const measure = () => {
      const h = flyerPreviewRef.current?.scrollHeight;
      if (h && h > 0) setFlyerHeight(h);
    };
    measure();
    const t1 = setTimeout(measure, 100);
    const t2 = setTimeout(measure, 300);
    const t3 = setTimeout(measure, 800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [generatedHtml]);

  // Measure container and calculate scale for flyer preview
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container) return;

    const updateScale = () => {
      const containerWidth = container.clientWidth - 24;
      const widthScale = containerWidth / FORMAT_WIDTH;
      const isMobile = window.innerWidth < 1024;

      // For fixed-height formats (social posts/stories), height is known up-front.
      // For variable-height (flyer), measure the rendered scroll height.
      const flyerActualHeight = HAS_FIXED_HEIGHT
        ? FORMAT_HEIGHT
        : (flyerPreviewRef.current?.scrollHeight || flyerHeight || 800);

      let scale;
      if (isSplit) {
        // Split pane is height-constrained — fit inside the visible right pane.
        // The CSS gives the right pane an explicit size (50vh on mobile, h-full on
        // desktop) so we trust the measured height. The viewport-bound is only a
        // fallback for the rare case where measurement returns 0 (initial mount).
        const measuredHeight = (container.clientHeight || 0) - 32;
        const isMobileSplit = window.innerWidth < 768; // matches the md: breakpoint
        const viewportBound = isMobileSplit
          ? window.innerHeight * 0.5            // matches CSS 50vh
          : window.innerHeight - 240;            // desktop budget
        const containerHeight = measuredHeight > 0 ? measuredHeight : viewportBound;
        const heightScale = containerHeight > 0 ? (containerHeight * 0.92) / flyerActualHeight : 0.6;
        // Cap depends on viewMode:
        //  - "mockup" — keep at 0.85 so the design fits comfortably inside the
        //    bigger phone screen (was 0.7 with the smaller frame).
        //  - "design" — go up to 1.0 (true size) since there's no phone chrome
        //    and the user explicitly chose to inspect the flyer.
        // 75% larger workspace: caps raised from 1.0 / 0.85 — still bounded
        // by the container, so small screens are unaffected.
        const scaleCap = viewMode === 'design' ? 1.75 : 1.5;
        scale = Math.min(scaleCap, widthScale, heightScale);
      } else if (isMobile) {
        const maxPreviewHeight = window.innerHeight * 0.42;
        const heightScale = maxPreviewHeight / flyerActualHeight;
        scale = Math.min(1, widthScale, heightScale);
      } else {
        const availableHeight = window.innerHeight - 315;
        const heightScale = availableHeight / flyerActualHeight;
        scale = Math.min(1, widthScale, heightScale);
      }

      scale = Math.max(0.15, scale);
      setPreviewScale(scale);
      setHasMeasuredScale(true);

      if (flyerPreviewRef.current && !HAS_FIXED_HEIGHT) {
        setFlyerHeight(flyerPreviewRef.current.scrollHeight);
      }
    };

    updateScale();
    const timer1 = setTimeout(updateScale, 150);
    const timer2 = setTimeout(updateScale, 500);
    const observer = new ResizeObserver(updateScale);
    observer.observe(container);
    window.addEventListener('resize', updateScale);
    return () => { observer.disconnect(); clearTimeout(timer1); clearTimeout(timer2); window.removeEventListener('resize', updateScale); };
  }, [generatedHtml, isSplit, viewMode, isMobileViewport]);

  // ==========================================
  // AUTH
  // ==========================================
  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      ...(session?.access_token && { 'Authorization': `Bearer ${session.access_token}` })
    };
  };

  // ==========================================
  // IMAGE UPLOAD — supports adding multiple
  // ==========================================
  const handleImageUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const remaining = MAX_IMAGES - uploadedImages.length - selectedProducts.length;

    if (remaining <= 0) {
      setError(`Maximum ${MAX_IMAGES} images allowed${selectedProducts.length > 0 ? ' (catalog products count)' : ''}`);
      return;
    }

    const filesToProcess = files.slice(0, remaining);
    let errors = [];

    filesToProcess.forEach((file) => {
      if (!validTypes.includes(file.type)) {
        errors.push(`${file.name}: invalid format`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        errors.push(`${file.name}: exceeds 5MB`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Full = event.target.result;
        const base64Data = base64Full.split(',')[1];
        setUploadedImages(prev => {
          if (prev.length >= MAX_IMAGES) return prev;
          return [...prev, {
            base64: base64Data,
            type: file.type,
            name: file.name,
            preview: base64Full
          }];
        });
      };
      reader.readAsDataURL(file);
    });

    if (errors.length > 0) {
      setError(errors.join('. '));
    } else {
      setError('');
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [uploadedImages.length, selectedProducts.length]);

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  // ==========================================
  // UPLOAD IMAGE TO SUPABASE STORAGE (frontend)
  // ==========================================
  const uploadImageToStorage = async (img, index) => {
    const timestamp = Date.now();
    const ext = img.type.split('/')[1] || 'jpg';
    const filename = `product_${storeId}_${timestamp}_${index}.${ext === 'jpeg' ? 'jpg' : ext}`;

    const byteChars = atob(img.base64);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteArray[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([byteArray], { type: img.type });

    const { error: uploadError } = await supabase.storage
      .from('flyers')
      .upload(filename, blob, {
        contentType: img.type,
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      console.error(`Image #${index + 1} upload failed:`, uploadError);
      return null;
    }

    const { data: urlData } = supabase.storage
      .from('flyers')
      .getPublicUrl(filename);

    return urlData?.publicUrl || null;
  };

  // ==========================================
  // GENERATE FLYER
  // ==========================================
  const handleGenerate = async (overrides = {}) => {
    // Overrides let the auto-generate path pass the seeded prompt/images
    // directly, so we don't depend on React having committed setDescription /
    // setUploadedImages before this runs. Manual generation is wired as
    // onClick={handleGenerate}, which passes a SyntheticEvent here — guard so
    // we only treat a plain options object as overrides, never an event.
    const opts = (overrides && !overrides.nativeEvent && typeof overrides === 'object') ? overrides : {};
    const effectiveProducts = opts.products ?? selectedProducts;
    const effectiveDescription = briefFor(opts.description ?? description, effectiveProducts);
    const effectiveImages = opts.images ?? uploadedImages;

    if (!effectiveDescription) {
      setError('Describe the flyer you want, or pick a product from your catalog');
      return;
    }
    // No prompt typed, only products picked: show the brief we generated from
    // so "Your prompt" and the saved design aren't blank.
    if (!(opts.description ?? description).trim()) setDescription(effectiveDescription);

    setIsGenerating(true);
    setError('');
    setGeneratedHtml('');
    setHasMeasuredScale(false);

    try {
      let imageUrls = [];
      if (effectiveImages.length > 0) {
        // Photos restored from a saved design are already hosted — reuse
        // their URL instead of re-uploading (they have no file data to send).
        const uploadPromises = effectiveImages.map((img, i) => (
          img.url ? Promise.resolve(img.url) : uploadImageToStorage(img, i)
        ));
        const results = await Promise.all(uploadPromises);
        imageUrls = results.filter(Boolean);

        if (imageUrls.length === 0 && effectiveImages.length > 0) {
          setError('Failed to upload images. Make sure the "flyers" storage bucket exists and is public.');
          setIsGenerating(false);
          return;
        }

        console.log(`✅ ${imageUrls.length}/${effectiveImages.length} images uploaded`);
      }

      const authHeaders = await getAuthHeaders();
      const fullDescription = editNote.trim()
        ? `${effectiveDescription}\n\nEDIT REQUEST: ${editNote.trim()}`
        : effectiveDescription;
      const payload = {
        description: fullDescription,
        store_id: storeId,
        business_name: businessName,
        business_info: businessInfo,
        // AI design style preset — backend uses this to pick the font pairing,
        // palette direction, and layout approach. Defaults to 'editorial'.
        style: selectedStyle,
        // NEW: format context — backend should use these to design at the right
        // dimensions and adapt copy/style to the platform.
        format: {
          id: format?.id || 'flyer',
          label: FORMAT_LABEL,
          width: FORMAT_WIDTH,
          height: FORMAT_HEIGHT,
          aspect_ratio: format?.aspectRatio || null,
          platform: FORMAT_PLATFORM,
          delivery_mode: format?.deliveryMode || 'sms',
        },
      };

      if (imageUrls.length > 0) {
        payload.image_urls = imageUrls;
      }
      // Catalog picks go as references only — the backend reads the photo
      // and the facts from the database.
      if (effectiveProducts.length > 0) {
        payload.products = effectiveProducts.map((p) => ({ sku: p.sku, brand: p.brand }));
      }

      const response = await fetch('/api/generate-flyer', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload)
      });

      // Defensive parsing — /api/* may not be reachable in plain `vite dev`
      // (serverless funcs only run under `vercel dev` or in deploy). Read text
      // first so we can give a useful error instead of a SyntaxError stack.
      const rawText = await response.text();
      let data = null;
      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch (parseErr) {
        console.error('Flyer API returned non-JSON:', rawText.slice(0, 300));
      }

      if (!response.ok || !data) {
        let msg = (data && (data.error || data.message)) || `Server error (${response.status})`;
        if (response.status === 404) {
          msg = 'Flyer service is unreachable (/api/generate-flyer 404). If you are running Vite locally, switch to `vercel dev` so serverless functions resolve.';
        } else if (response.status === 401 || response.status === 403) {
          msg = 'Not authorised — try refreshing and signing in again.';
        } else if (response.status >= 500) {
          msg = `Flyer service error (${response.status}). Try again in a moment.`;
        }
        setError(msg);
        return;
      }

      if (data.success && data.html) {
        const widthCap = FORMAT_WIDTH - 2;
        let processedHtml = data.html
          .replace(/width\s*:\s*(\d+)(px)?/gi, (match, num) => {
            return parseInt(num) > widthCap ? `width: 100%` : match;
          })
          .replace(/min-width\s*:\s*(\d+)(px)?/gi, (match, num) => {
            return parseInt(num) > widthCap ? `min-width: 100%` : match;
          });

        const heightStyle = HAS_FIXED_HEIGHT
          ? `height:${FORMAT_HEIGHT}px;max-height:${FORMAT_HEIGHT}px;min-height:${FORMAT_HEIGHT}px;`
          : '';
        const innerHeightStyle = HAS_FIXED_HEIGHT ? `height:${FORMAT_HEIGHT}px;` : '';

        const constrainedHtml = `<div style="width:${FORMAT_WIDTH}px;max-width:${FORMAT_WIDTH}px;${heightStyle}overflow:hidden;margin:0;padding:0;box-sizing:border-box;">
          <style>
            .flyer-root, .flyer-root * { box-sizing: border-box !important; max-width: ${FORMAT_WIDTH}px !important; }
            .flyer-root img, .flyer-root video, .flyer-root svg { max-width: 100% !important; height: auto !important; }
            .flyer-root table { table-layout: fixed !important; width: 100% !important; }
            .flyer-root h1, .flyer-root h2, .flyer-root h3, .flyer-root h4, .flyer-root p, .flyer-root span, .flyer-root div {
              overflow-wrap: break-word !important; word-wrap: break-word !important; word-break: break-word !important;
            }
          </style>
          <div class="flyer-root" style="width:${FORMAT_WIDTH}px;max-width:${FORMAT_WIDTH}px;overflow:hidden;${innerHeightStyle}">${processedHtml}</div>
        </div>`;
        lastImageUrlsRef.current = Array.isArray(data.image_urls) ? data.image_urls : imageUrls;
        setGeneratedHtml(constrainedHtml);
        setEditNote('');
      } else {
        setError(data.error || `Failed to generate ${FORMAT_LABEL.toLowerCase()}`);
      }
    } catch (err) {
      console.error('Canvas generation error:', err);
      setError('Network error — please try again');
    } finally {
      setIsGenerating(false);
    }
  };

  // ==========================================
  // CAPTURE TO PNG — offscreen clone at native size for the active format
  // ==========================================
  const captureFlyer = async () => {
    const offscreen = document.createElement('div');
    const heightCss = HAS_FIXED_HEIGHT
      ? `height: ${FORMAT_HEIGHT}px; max-height: ${FORMAT_HEIGHT}px;`
      : '';
    offscreen.style.cssText = `
      position: fixed; top: -10000px; left: -10000px;
      width: ${FORMAT_WIDTH}px; max-width: ${FORMAT_WIDTH}px;
      ${heightCss}
      background: #fff; overflow: hidden;
      z-index: -1; pointer-events: none;
    `;
    offscreen.innerHTML = generatedHtml;
    document.body.appendChild(offscreen);

    // ============================================================
    // Wait for ALL assets to load before rasterising.
    // html2canvas's createPattern fails with "image argument is a canvas
    // element with a width or height of 0" if it tries to capture an <img>
    // whose naturalWidth is still 0 — i.e. the image is still loading.
    // Three sources we need to wait for:
    //   1. Google Fonts (@import in the AI-generated <style>)
    //   2. Product photos (Supabase Storage URLs)
    //   3. Iconify SVGs (api.iconify.design URLs)
    // ============================================================

    // Tiny initial yield so the browser can attach <img> elements to the DOM
    // and start their requests.
    await new Promise(r => setTimeout(r, 50));

    // (1) Fonts
    try { await document.fonts?.ready; } catch (_) { /* ignore */ }

    // (2) + (3) Wait for every <img> to either complete or time out.
    // Images that errored (404, CORS, dead URL) resolve too — we don't want
    // a single broken icon URL to block the entire capture.
    const images = Array.from(offscreen.querySelectorAll('img'));
    const PER_IMAGE_TIMEOUT_MS = 4000;
    await Promise.all(images.map((img) => {
      // Already loaded (cached, or instant data: URI)
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        let done = false;
        const finish = () => { if (!done) { done = true; resolve(); } };
        img.addEventListener('load', finish, { once: true });
        img.addEventListener('error', finish, { once: true });
        // Hard timeout so a single hung asset doesn't lock auto-save forever
        setTimeout(finish, PER_IMAGE_TIMEOUT_MS);
      });
    }));

    // Final settle frame — ensures layout has reflowed with the now-loaded
    // images and fonts before html2canvas reads geometry.
    await new Promise(r => setTimeout(r, 150));

    const captureHeight = HAS_FIXED_HEIGHT ? FORMAT_HEIGHT : offscreen.scrollHeight;

    // Defensive: if the offscreen div somehow still has zero height (e.g.
    // generated HTML is broken and renders nothing), abort cleanly instead
    // of letting html2canvas throw the cryptic createPattern error.
    if (!captureHeight || captureHeight < 1 || !FORMAT_WIDTH || FORMAT_WIDTH < 1) {
      document.body.removeChild(offscreen);
      throw new Error('Design has no measurable size — try regenerating.');
    }

    const html2canvasOptions = {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: FORMAT_WIDTH,
      height: captureHeight,
      scrollX: 0,
      scrollY: 0,
      windowWidth: FORMAT_WIDTH,
      windowHeight: captureHeight,
      letterRendering: true,
      imageTimeout: PER_IMAGE_TIMEOUT_MS,
    };

    let canvas;
    try {
      canvas = await html2canvas(offscreen, html2canvasOptions);
    } catch (err) {
      // First-pass crash. The classic culprit is html2canvas's `createPattern`
      // throwing on an <img> or <svg> that resolved to 0×0 — a broken Iconify
      // URL, a CORS-blocked product image, or an inline SVG without explicit
      // width/height attributes. Sanitize those nodes and retry once.
      console.warn('html2canvas first pass failed, sanitizing DOM and retrying:', err?.message || err);

      Array.from(offscreen.querySelectorAll('img')).forEach((img) => {
        const broken = !img.complete || img.naturalWidth === 0 || img.naturalHeight === 0;
        if (broken) {
          // Drop the source so html2canvas doesn't try to re-pattern it,
          // and give the element explicit (tiny) dimensions so layout still works.
          img.removeAttribute('src');
          img.removeAttribute('srcset');
          img.style.width = img.style.width || '1px';
          img.style.height = img.style.height || '1px';
          img.style.opacity = '0';
        }
      });

      Array.from(offscreen.querySelectorAll('svg')).forEach((svg) => {
        const w = svg.getAttribute('width') || svg.style.width;
        const h = svg.getAttribute('height') || svg.style.height;
        if (!w) svg.setAttribute('width', '1');
        if (!h) svg.setAttribute('height', '1');
      });

      try {
        canvas = await html2canvas(offscreen, {
          ...html2canvasOptions,
          // foreignObjectRendering: false is the conservative path —
          // slower but doesn't trip the createPattern bug as easily.
          foreignObjectRendering: false,
        });
      } catch (retryErr) {
        document.body.removeChild(offscreen);
        console.error('html2canvas retry also failed:', retryErr?.message || retryErr);
        throw new Error('Could not export this design. Try regenerating — the AI may have produced a broken image or icon.');
      }
    }

    document.body.removeChild(offscreen);
    return canvas;
  };

  const handleApproveAndSend = async () => {
    if (!generatedHtml) return;

    setIsCapturing(true);
    setError('');

    try {
      const canvas = await captureFlyer();
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/png', 0.95);
      });

      if (!blob) throw new Error('Failed to capture flyer image');

      const timestamp = Date.now();
      const filename = `flyer_${storeId}_${timestamp}.png`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('flyers')
        .upload(filename, blob, {
          contentType: 'image/png',
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        if (uploadError.message?.includes('not found') || uploadError.statusCode === 404) {
          throw new Error('Storage bucket "flyers" not found. Please create it in Supabase Dashboard → Storage with public access enabled.');
        }
        throw uploadError;
      }

      const { data: urlData } = supabase.storage
        .from('flyers')
        .getPublicUrl(filename);

      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error('Failed to get public URL for flyer');

      console.log('✅ Flyer uploaded:', publicUrl);

      if (onFlyerReady) onFlyerReady(publicUrl);
      if (addNotification) addNotification('Flyer created! Ready to send.', 'success');

    } catch (err) {
      console.error('Flyer capture/upload error:', err);
      setError(err.message || 'Failed to process flyer — please try again');
    } finally {
      setIsCapturing(false);
    }
  };

  // For social formats — capture the canvas and download as PNG immediately.
  // No Supabase upload, no SMS flow. The user posts manually to their socials.
  const handleDirectDownload = async () => {
    if (!generatedHtml) return;

    setIsCapturing(true);
    setError('');

    try {
      const canvas = await captureFlyer();
      const link = document.createElement('a');
      const safeId = (format?.id || 'design').replace(/[^a-z0-9_-]/gi, '');
      link.download = `${safeId}_${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      if (addNotification) {
        const platformName = {
          instagram: 'Instagram',
          facebook: 'Facebook',
          tiktok: 'TikTok',
          video: 'your platform',
        }[FORMAT_PLATFORM] || 'your socials';
        addNotification(`Downloaded! Now post to ${platformName}.`, 'success');
      }
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message || 'Failed to download — please try again');
    } finally {
      setIsCapturing(false);
    }
  };

  // ==========================================
  // QUICK PROMPTS — broader range for hero presentation
  // ==========================================
  const promptSuggestions = isHero
    ? [
        '🔥 Memorial Day blowout — 40% off all sectionals, this weekend only',
        '🚚 Free white-glove delivery on orders over $999',
        '🛏️ New arrivals — modern bedroom collections under $1,500',
        '🎉 Grand opening — Saturday only, doorbusters every hour',
        '💥 Clearance event — last chance, everything must go',
        '✨ Black Friday preview — early access for VIP customers',
      ]
    : [
        '🔥 Weekend sale — 30% off all sofas',
        '🚚 Free delivery this month only',
        '🆕 New arrivals — modern bedroom sets',
        '🎉 Grand opening celebration',
        '💥 Clearance event — everything must go',
        '🎄 Holiday special — gift ideas',
      ];

  // ==========================================
  // CATALOG PRODUCT PICKER — one instance, placed by every variant below
  // ==========================================
  const productPicker = (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-0.5">Products</p>
      <CatalogProductPicker
        products={selectedProducts}
        onChange={(next) => { setSelectedProducts(next); setError(''); }}
        max={MAX_IMAGES - uploadedImages.length}
        disabled={isGenerating}
      />
    </div>
  );

  // ==========================================
  // RENDER
  // ==========================================

  // ── SPLIT VARIANT — premium two-pane studio (left = controls, right = preview) ──
  // Both panes are always visible; content of each pane swaps based on internal state.
  if (isSplit) {
    const hasOutput = Boolean(generatedHtml) && !isGenerating;
    const FadeKeyframes = () => (
      <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    );

    // ──────────────────────────────────────────────────────────────────────
    // MOBILE SPLIT — Canva-style single-column editor (<768px)
    // The phone is already a phone — no nested PhoneFrameSplit. Canvas is
    // full-bleed at the top, controls flow below, primary action sticks to
    // the bottom inside a safe-area-aware bar. Refs (previewContainerRef /
    // flyerPreviewRef) attach here when this branch renders; the existing
    // updateScale effect handles the rest because isMobileViewport is in
    // its dep array.
    // ──────────────────────────────────────────────────────────────────────
    if (isMobileViewport) {
      return (
        <div className="flex flex-col h-full min-h-0">
          <FadeKeyframes />
          {/* SCROLLABLE BODY — canvas + controls all in one column */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            <div className="px-4 pt-3 pb-4 space-y-4">

              {leftHeader && <div>{leftHeader}</div>}

              {/* ===== CANVAS / HINT =====
                  hasOutput → full canvas preview (the focus of generated state)
                  !hasOutput → compact icon+title hint, NO big placeholder card.
                  The old dashed-border placeholder pushed the prompt textarea
                  below the fold, which made the screen feel empty and forced
                  unnecessary scrolling on empty state. */}
              {hasOutput ? (
                <>
                  <div
                    ref={previewContainerRef}
                    className="w-full bg-white rounded-2xl shadow-sm ring-1 ring-slate-200 overflow-hidden flex items-center justify-center"
                    style={{ minHeight: '40vh', maxHeight: '55vh' }}
                  >
                    <div style={{
                      width: FORMAT_WIDTH * previewScale,
                      height: (HAS_FIXED_HEIGHT
                        ? FORMAT_HEIGHT
                        : (flyerPreviewRef.current?.scrollHeight || flyerHeight)
                      ) * previewScale,
                      position: 'relative',
                      minHeight: 40,
                      maxWidth: '100%',
                    }}>
                      <div style={{
                        width: FORMAT_WIDTH,
                        transform: `scale(${previewScale})`,
                        transformOrigin: 'top left',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                      }}>
                        <div
                          ref={flyerPreviewRef}
                          dangerouslySetInnerHTML={{ __html: generatedHtml }}
                          style={{
                            width: FORMAT_WIDTH,
                            maxWidth: FORMAT_WIDTH,
                            ...(HAS_FIXED_HEIGHT ? { height: FORMAT_HEIGHT, maxHeight: FORMAT_HEIGHT } : {}),
                            background: '#fff',
                            overflow: 'hidden',
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Format meta — only shown under the actual canvas */}
                  {format && (
                    <div className="flex items-center justify-center gap-2 -mt-1">
                      <span className="text-[11px] font-semibold text-slate-600">{FORMAT_LABEL}</span>
                      {format.width && format.height && (
                        <span className="text-[10px] text-slate-400 font-mono tabular-nums">
                          {format.width} × {format.height}
                        </span>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-3 px-0.5">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-sm shadow-blue-600/20 flex-shrink-0">
                    <Sparkles className={`w-5 h-5 text-white ${isGenerating ? 'animate-pulse' : ''}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[15px] font-bold text-slate-900 leading-tight">
                      {isGenerating
                        ? `Designing your ${FORMAT_LABEL.toLowerCase()}…`
                        : `Design a ${FORMAT_LABEL.toLowerCase()} with AI`}
                    </p>
                    <p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug">
                      {isGenerating
                        ? 'Usually takes 10–20 seconds'
                        : 'Describe what you want below — AI handles the rest'}
                    </p>
                  </div>
                </div>
              )}

              {/* ===== STYLE PICKER ===== */}
              <div className="space-y-1.5">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-0.5">Style</p>
                <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4">
                  {STYLES.map((s) => {
                    const active = selectedStyle === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSelectedStyle(s.id)}
                        title={s.hint}
                        className={`flex-shrink-0 px-3.5 py-2 rounded-full text-[13px] font-semibold transition-all border ${
                          active
                            ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                            : 'bg-white text-slate-600 border-slate-200 active:bg-slate-50'
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ===== CATALOG PRODUCTS ===== */}
              {productPicker}

              {/* ===== ORIGINAL PROMPT (when we have output) ===== */}
              {hasOutput && description && (
                <div className="rounded-2xl bg-slate-50 border border-slate-200 px-3.5 py-2.5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Your prompt</p>
                  <p className="text-[14px] text-slate-700 leading-relaxed">{description}</p>
                </div>
              )}

              {/* ===== PROMPT / REFINE TEXTAREA ===== */}
              {/* text-base (16px) prevents iOS auto-zoom on focus. */}
              <div className="relative">
                <div className="bg-white border border-slate-200 focus-within:border-blue-300 rounded-2xl shadow-sm overflow-hidden">
                  {hasOutput ? (
                    <textarea
                      value={editNote}
                      onChange={(e) => setEditNote(e.target.value)}
                      placeholder="Refine — &ldquo;make the headline bigger&rdquo;, &ldquo;swap to a navy palette&rdquo;…"
                      className="w-full h-24 px-4 pt-3.5 pb-2 bg-transparent text-base text-slate-900 placeholder-slate-400 resize-none focus:outline-none leading-relaxed"
                      maxLength={500}
                    />
                  ) : (
                    <textarea
                      value={description}
                      onChange={(e) => { setDescription(e.target.value); setError(''); }}
                      placeholder="Describe what you want — &ldquo;Memorial Day sale, 40% off sofas, dark navy with gold accents&rdquo;"
                      className="w-full h-32 px-4 pt-4 pb-2 bg-transparent text-base text-slate-900 placeholder-slate-400 resize-none focus:outline-none leading-relaxed"
                      maxLength={2000}
                    />
                  )}

                  {/* Image strip + counter */}
                  <div className="flex items-center justify-between gap-2 px-3 pb-2.5 pt-1 border-t border-slate-100">
                    <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                      {uploadedImages.map((img, i) => (
                        <div key={i} className="relative w-9 h-9 rounded-md overflow-hidden border border-slate-200 bg-white flex-shrink-0">
                          <img src={img.preview} alt={img.name} className="w-full h-full object-cover" />
                          <button
                            onClick={() => removeImage(i)}
                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-slate-900 text-white flex items-center justify-center shadow"
                            aria-label="Remove image"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                      {imageSlotsLeft > 0 && (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="w-9 h-9 rounded-md border border-dashed border-slate-300 active:bg-blue-50/40 flex items-center justify-center flex-shrink-0"
                          title={`Attach product photos (${uploadedImages.length}/${MAX_IMAGES})`}
                        >
                          <Plus className="w-4 h-4 text-slate-400" />
                        </button>
                      )}
                      <span className="text-[11px] text-slate-400 ml-0.5 truncate">
                        {uploadedImages.length > 0
                          ? `${uploadedImages.length}/${MAX_IMAGES} photos`
                          : 'Add photos'}
                      </span>
                    </div>
                    <span className="text-[11px] text-slate-400 flex-shrink-0 tabular-nums">
                      {hasOutput ? `${editNote.length}/500` : `${description.length}/2000`}
                    </span>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleImageUpload}
                    multiple
                    className="hidden"
                  />
                </div>
              </div>

              {/* ===== QUICK IDEAS (empty state only) ===== */}
              {!description && !hasOutput && (
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 px-0.5">Try one of these</p>
                  <div className="flex flex-wrap gap-1.5">
                    {promptSuggestions.map((prompt, i) => (
                      <button
                        key={i}
                        onClick={() => setDescription(prompt)}
                        className="px-3 py-1.5 bg-white active:bg-blue-50 border border-slate-200 rounded-full text-[12px] text-slate-600 transition-all shadow-sm text-left"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ===== ERROR ===== */}
              {error && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border bg-red-50 border-red-200">
                  <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <span className="text-[13px] font-medium text-red-700">{error}</span>
                </div>
              )}

              {/* spacer so last control isn't hidden behind the sticky bar */}
              <div className="h-1" />
            </div>
          </div>

          {/* ===== PINNED BOTTOM ACTION BAR =====
              flex-shrink-0 inside the parent flex column anchors this to
              the bottom of the editor area. The mobile dashboard's bottom
              nav (rendered below us) handles its own safe-area-inset, so
              this bar uses plain symmetric padding. */}
          <div className="flex-shrink-0 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3">
            {hasOutput ? (
              <div className="space-y-2">
                {/* Row 1 — refine / regenerate */}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className={`w-full py-3 rounded-xl font-semibold text-[14px] flex items-center justify-center gap-1.5 transition-all ${
                    editNote.trim()
                      ? 'bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-600/20'
                      : 'bg-white border border-slate-200 active:bg-slate-50 text-slate-700'
                  } ${isGenerating ? 'opacity-50' : ''}`}
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Designing…</span>
                    </>
                  ) : editNote.trim() ? (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>Apply changes</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      <span>Regenerate</span>
                    </>
                  )}
                </button>

                {/* Row 2 — primary CTA (varies by format) + download */}
                <div className="flex gap-2">
                  {IS_CURRENT_DEAL ? (
                    <button
                      onClick={handleSaveDeal}
                      disabled={isSavingDeal || !generatedHtml}
                      className={`flex-1 py-3 rounded-xl font-semibold text-[14px] flex items-center justify-center gap-1.5 transition-all ${
                        dealSaved
                          ? 'bg-emerald-600 text-white shadow-md'
                          : isSavingDeal
                            ? 'bg-amber-400 text-white cursor-wait'
                            : 'bg-amber-600 active:bg-amber-700 text-white shadow-md'
                      }`}
                    >
                      {isSavingDeal ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Saving…</span>
                        </>
                      ) : dealSaved ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Saved · live in FAQ</span>
                        </>
                      ) : (
                        <>
                          <Tag className="w-4 h-4" />
                          <span>Save deal</span>
                        </>
                      )}
                    </button>
                  ) : !IS_SOCIAL ? (
                    <button
                      onClick={handleApproveAndSend}
                      disabled={isCapturing}
                      className={`flex-1 py-3 rounded-xl font-semibold text-[14px] flex items-center justify-center gap-1.5 transition-all ${
                        isCapturing
                          ? 'bg-blue-400 text-white cursor-wait'
                          : 'bg-blue-600 active:bg-blue-700 text-white shadow-md'
                      }`}
                    >
                      {isCapturing ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Sending…</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>Send via SMS</span>
                        </>
                      )}
                    </button>
                  ) : null}
                  <button
                    onClick={handleDirectDownload}
                    disabled={isCapturing}
                    className={`${IS_SOCIAL ? 'flex-1' : 'flex-shrink-0 px-4'} py-3 rounded-xl font-semibold text-[14px] flex items-center justify-center gap-1.5 transition-all ${
                      IS_SOCIAL
                        ? 'bg-blue-600 active:bg-blue-700 text-white shadow-md'
                        : 'bg-white border border-slate-200 active:bg-slate-50 text-slate-700'
                    } ${isCapturing ? 'opacity-50 cursor-wait' : ''}`}
                  >
                    <Download className="w-4 h-4" />
                    <span>{
                      IS_SOCIAL && FORMAT_PLATFORM === 'instagram' ? 'Download for Instagram' :
                      IS_SOCIAL && FORMAT_PLATFORM === 'facebook'  ? 'Download for Facebook'  :
                      IS_SOCIAL && FORMAT_PLATFORM === 'tiktok'    ? 'Download for TikTok'    :
                      IS_SOCIAL && FORMAT_PLATFORM === 'video'     ? 'Download for Reel'      :
                      IS_SOCIAL ? 'Download' : 'PNG'
                    }</span>
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={handleGenerate}
                disabled={!canGenerate || isGenerating}
                className={`w-full py-3.5 rounded-2xl font-semibold text-[14px] flex items-center justify-center gap-2 transition-all ${
                  !canGenerate || isGenerating
                    ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                    : 'bg-gradient-to-br from-blue-600 to-indigo-600 active:from-blue-700 active:to-indigo-700 text-white shadow-lg shadow-blue-600/20'
                }`}
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Designing…</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>Generate with AI</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col lg:grid lg:grid-cols-[minmax(0,1.7fr)_minmax(0,3fr)] lg:grid-rows-[minmax(0,1fr)] lg:items-start gap-6 lg:gap-8 h-full min-h-0 overflow-y-auto studio-scroll lg:overflow-hidden">
        <FadeKeyframes />
        {/* ============ LEFT (mobile: BELOW preview) — controls ============ */}
        <div className="order-2 lg:order-1 flex flex-col lg:min-h-0 lg:h-[min(72vh,700px)]">
          <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto studio-scroll lg:pr-1 space-y-3">

            {leftHeader && <div className="lg:-mt-0.5">{leftHeader}</div>}

            {/* Section label */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-sm">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider leading-none">AI Designer</p>
                <p className="text-[11px] text-slate-400 leading-tight mt-0.5">
                  {hasOutput ? 'Refine the design or start over' : 'Describe your promotion'}
                </p>
              </div>
            </div>

            {/* Style picker — horizontal scroll on mobile, wraps on desktop */}
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-0.5">Style</p>
              <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-0.5 px-0.5 md:flex-wrap md:overflow-visible">
                {STYLES.map((s) => {
                  const active = selectedStyle === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSelectedStyle(s.id)}
                      title={s.hint}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all border ${
                        active
                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-800'
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Catalog products — pick by name or item number */}
            {productPicker}

            {/* Original prompt — shown as a "message" once we have output */}
            {hasOutput && description && (
              <div className="rounded-2xl bg-slate-50 border border-slate-200 px-3.5 py-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Your prompt</span>
                </div>
                <p className="text-[13px] text-slate-700 leading-relaxed">{description}</p>
              </div>
            )}

            {/* Primary prompt input — always editable */}
            <div className="relative group">
              <div className="absolute -inset-px bg-gradient-to-br from-blue-500/30 via-indigo-500/20 to-purple-500/30 rounded-2xl opacity-0 group-focus-within:opacity-100 blur-md transition-opacity duration-500" />
              <div className="relative bg-white border border-slate-200 group-focus-within:border-blue-300 rounded-2xl shadow-sm transition-colors overflow-hidden">
                {hasOutput ? (
                  <textarea
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && editNote.trim()) {
                        e.preventDefault();
                        handleGenerate();
                      }
                    }}
                    placeholder="Refine — &ldquo;make the headline bigger&rdquo;, &ldquo;swap to a navy palette&rdquo;, &ldquo;add a 50% off badge&rdquo;…"
                    className="w-full h-24 px-4 pt-3.5 pb-2 bg-transparent text-[14px] text-slate-900 placeholder-slate-400 resize-none focus:outline-none leading-relaxed"
                    maxLength={500}
                  />
                ) : (
                  <textarea
                    value={description}
                    onChange={(e) => { setDescription(e.target.value); setError(''); }}
                    placeholder="Describe what you want — &ldquo;Memorial Day sale flyer, 40% off sofas, dark navy with gold accents, urgent feel&rdquo;"
                    className="w-full h-32 px-4 pt-4 pb-2 bg-transparent text-[14px] text-slate-900 placeholder-slate-400 resize-none focus:outline-none leading-relaxed"
                    maxLength={2000}
                  />
                )}

                {/* Inline image strip + counter */}
                <div className="flex items-center justify-between gap-2 px-3 pb-2.5 pt-1 border-t border-slate-100">
                  <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                    {uploadedImages.map((img, i) => (
                      <div key={i} className="relative group/img w-8 h-8 rounded-md overflow-hidden border border-slate-200 bg-white flex-shrink-0">
                        <img src={img.preview} alt={img.name} className="w-full h-full object-cover" />
                        <button
                          onClick={() => removeImage(i)}
                          className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity"
                          aria-label="Remove image"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                    {imageSlotsLeft > 0 && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-8 h-8 rounded-md border border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/40 flex items-center justify-center transition-all flex-shrink-0"
                        title={`Attach product photos (${uploadedImages.length}/${MAX_IMAGES})`}
                      >
                        <Plus className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                    )}
                    <span className="text-[10.5px] text-slate-400 ml-0.5 truncate">
                      {uploadedImages.length > 0
                        ? `${uploadedImages.length}/${MAX_IMAGES} attached`
                        : 'Attach product photos'}
                    </span>
                  </div>
                  <span className="text-[10.5px] text-slate-400 flex-shrink-0 tabular-nums">
                    {hasOutput ? `${editNote.length}/500` : `${description.length}/2000`}
                  </span>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageUpload}
                  multiple
                  className="hidden"
                />
              </div>
            </div>

            {/* Quick ideas — only on empty state */}
            {!description && !hasOutput && (
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 ml-1">Try one of these</p>
                <div className="flex flex-wrap gap-1.5">
                  {promptSuggestions.map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => setDescription(prompt)}
                      className="px-2.5 py-1 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-full text-[11px] text-slate-600 hover:text-blue-700 transition-all shadow-sm text-left"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-red-50 border-red-200">
                <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                <span className="text-[12px] font-medium text-red-700">{error}</span>
              </div>
            )}
          </div>

          {/* Action bar — sticky on mobile (always reachable), pinned to bottom of left pane on desktop */}
          <div className="sticky bottom-0 z-10 md:static bg-slate-50/95 backdrop-blur md:bg-transparent md:backdrop-blur-none flex-shrink-0 pt-3 md:pt-2.5 mt-3 md:mt-2 pb-1 md:pb-0 border-t border-slate-200 space-y-2 -mx-1 md:mx-0 px-1 md:px-0">
            {hasOutput ? (
              <>
                <div className="flex gap-1.5">
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className={`flex-1 py-2.5 rounded-xl font-semibold text-[13px] flex items-center justify-center gap-1.5 transition-all ${
                      editNote.trim()
                        ? 'bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md shadow-blue-600/20'
                        : 'bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    {editNote.trim() ? (
                      <>
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>Apply changes</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3.5 h-3.5" />
                        <span>Regenerate</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="flex gap-1.5">
                  {/* Primary CTA depends on the format:
                      - Current Deal → "Save deal" (auto-publishes to phone agent FAQ)
                      - SMS-deliverable (Flyer/MMS) → "Send via SMS"
                      - Social formats → no primary action; download is the CTA below */}
                  {IS_CURRENT_DEAL ? (
                    <button
                      onClick={handleSaveDeal}
                      disabled={isSavingDeal || !generatedHtml}
                      title="Save this deal — it'll appear in your phone agent's FAQ"
                      className={`flex-1 py-3 rounded-xl font-semibold text-[13px] flex items-center justify-center gap-1.5 transition-all ${
                        dealSaved
                          ? 'bg-emerald-600 text-white shadow-md'
                          : isSavingDeal
                            ? 'bg-amber-400 text-white cursor-wait'
                            : 'bg-amber-600 hover:bg-amber-700 text-white shadow-md'
                      }`}
                    >
                      {isSavingDeal ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          <span>Saving…</span>
                        </>
                      ) : dealSaved ? (
                        <>
                          <Check className="w-3.5 h-3.5" />
                          <span>Saved · live in FAQ</span>
                        </>
                      ) : (
                        <>
                          <Tag className="w-3.5 h-3.5" />
                          <span>Save deal</span>
                        </>
                      )}
                    </button>
                  ) : !IS_SOCIAL ? (
                    <button
                      onClick={handleApproveAndSend}
                      disabled={isCapturing}
                      title="Send this design to customers via SMS"
                      className={`flex-1 py-3 rounded-xl font-semibold text-[13px] flex items-center justify-center gap-1.5 transition-all ${
                        isCapturing
                          ? 'bg-blue-400 text-white cursor-wait'
                          : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'
                      }`}
                    >
                      {isCapturing ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          <span>Sending…</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-3.5 h-3.5" />
                          <span>Send via SMS</span>
                        </>
                      )}
                    </button>
                  ) : null}
                  <button
                    onClick={handleDirectDownload}
                    disabled={isCapturing}
                    title={`Download as PNG for manual ${IS_SOCIAL ? FORMAT_LABEL.toLowerCase() : 'use'}`}
                    className={`flex-1 py-3 rounded-xl font-semibold text-[13px] flex items-center justify-center gap-1.5 transition-all ${
                      IS_SOCIAL
                        // Social formats: download IS the primary action. Use the
                        // prominent blue treatment so the user has a clear next step.
                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'
                        : 'bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'
                    } ${isCapturing ? 'opacity-50 cursor-wait' : ''}`}
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>{
                      // Platform-aware label — clearer than generic "Download"
                      // when the user has just designed for a specific platform.
                      IS_SOCIAL && FORMAT_PLATFORM === 'instagram' ? 'Download for Instagram' :
                      IS_SOCIAL && FORMAT_PLATFORM === 'facebook'  ? 'Download for Facebook'  :
                      IS_SOCIAL && FORMAT_PLATFORM === 'tiktok'    ? 'Download for TikTok'    :
                      IS_SOCIAL && FORMAT_PLATFORM === 'video'     ? 'Download for Reel'      :
                      'Download'
                    }</span>
                  </button>
                </div>
              </>
            ) : (
              <button
                onClick={handleGenerate}
                disabled={!canGenerate || isGenerating}
                className={`w-full py-3.5 rounded-2xl font-semibold text-[13px] flex items-center justify-center gap-2 transition-all ${
                  !canGenerate || isGenerating
                    ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                    : 'bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-blue-600/30'
                }`}
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Designing…</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>Generate with AI</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* ============ RIGHT (mobile: ABOVE controls) — workspace card matches picker ============ */}
        <div className="order-1 lg:order-2 min-w-0 lg:min-h-0">
          <div className="lg:sticky lg:top-2 bg-gradient-to-b from-slate-50 to-white border border-slate-200 rounded-2xl p-5 flex flex-col min-h-0 overflow-hidden h-full lg:h-[min(72vh,700px)] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)]">
            {/* WORKSPACE header — same as picker */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded bg-slate-100 border border-slate-200 flex items-center justify-center">
                  <Sparkles className="w-2.5 h-2.5 text-slate-500" />
                </div>
                <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Workspace</h3>
              </div>

              <div className="flex items-center gap-2">
                {/* View toggle — only shown once a real design exists. Slides
                    between the flyer alone (default first view) and the
                    phone-mockup framing, so the user sees the full design
                    first and can flip to the contextual mockup on demand. */}
                {generatedHtml && !isGenerating && (
                  <div
                    role="tablist"
                    aria-label="Preview mode"
                    className="inline-flex p-0.5 bg-slate-100 border border-slate-200 rounded-full text-[10px] font-semibold"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={viewMode === 'design'}
                      onClick={() => setViewMode('design')}
                      className={`px-2.5 py-1 rounded-full transition-all ${
                        viewMode === 'design'
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      Design
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={viewMode === 'mockup'}
                      onClick={() => setViewMode('mockup')}
                      className={`px-2.5 py-1 rounded-full transition-all ${
                        viewMode === 'mockup'
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      Mockup
                    </button>
                  </div>
                )}

                {/* Status pill — quietly reflects current state */}
                {isGenerating ? (
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-[10px] font-semibold text-blue-700">
                    <Sparkles className="w-2.5 h-2.5 animate-pulse" />
                    <span>Designing…</span>
                  </div>
                ) : generatedHtml ? (
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-semibold text-emerald-700">
                    <span>Ready</span>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Phone + caption block — ScaleToFit shrinks the fixed-size phone
                to fit the available height without clipping or distorting it.
                Only the design inside the phone changes. */}
            <div className="flex-1 min-h-0 flex flex-col items-center py-4 overflow-hidden">
              <div className="flex-1 min-h-0 w-full">
                <ScaleToFit baseWidth={300} baseHeight={620}>
                <div className="relative">
                {viewMode === 'design' && generatedHtml && !isGenerating ? (
                  // ===== DESIGN VIEW =====
                  // The flyer alone, no phone chrome. We match PhoneFrameSplit's
                  // 340-wide footprint so swapping between Mockup ↔ Design
                  // doesn't jump the layout. Same scale machinery as the
                  // mockup's renderScaled — we keep flyerPreviewRef on the
                  // actual rendered HTML so the height measurement effect
                  // continues to work, and previewContainerRef on the outer
                  // box so updateScale can re-measure when viewMode changes.
                  <div
                    ref={previewContainerRef}
                    className="bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 flex items-center justify-center overflow-hidden"
                    style={{ width: 300, height: 620 }}
                  >
                    <div style={{
                      width: FORMAT_WIDTH * previewScale,
                      height: (HAS_FIXED_HEIGHT
                        ? FORMAT_HEIGHT
                        : (flyerPreviewRef.current?.scrollHeight || flyerHeight)
                      ) * previewScale,
                      position: 'relative',
                      minHeight: 40,
                      maxWidth: '100%',
                    }}>
                      <div style={{
                        width: FORMAT_WIDTH,
                        transform: `scale(${previewScale})`,
                        transformOrigin: 'top left',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                      }}>
                        <div
                          ref={flyerPreviewRef}
                          dangerouslySetInnerHTML={{ __html: generatedHtml }}
                          style={{
                            width: FORMAT_WIDTH,
                            maxWidth: FORMAT_WIDTH,
                            ...(HAS_FIXED_HEIGHT ? { height: FORMAT_HEIGHT, maxHeight: FORMAT_HEIGHT } : {}),
                            background: '#fff',
                            overflow: 'hidden',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  // ===== MOCKUP VIEW =====
                  // Also hosts the empty + "Designing…" states regardless of
                  // viewMode; the design view above only takes over once a
                  // generated design actually exists.
                  <PhoneFrameSplit>
                  {(() => {
                    // Inner content states — drop into each wireframe's content slot
                    const renderEmpty = () => (
                      <div className="flex flex-col items-center gap-1.5 px-2 text-center">
                        <Image className="w-5 h-5 text-slate-300" />
                        <span className="text-[9px] font-semibold text-slate-400 leading-tight">
                          Your design here
                        </span>
                      </div>
                    );

                    const renderGenerating = () => (
                      <div className="flex flex-col items-center gap-1.5">
                        <div className="relative">
                          <div className="absolute inset-0 rounded-lg bg-blue-500/30 blur-md animate-pulse" />
                          <div className="relative w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow">
                            <Sparkles className="w-4 h-4 text-white animate-pulse" />
                          </div>
                        </div>
                        <span className="text-[9px] font-semibold text-slate-500">Designing…</span>
                      </div>
                    );

                    const renderScaled = () => (
                      <div
                        style={{
                          width: FORMAT_WIDTH * previewScale,
                          height: (HAS_FIXED_HEIGHT
                            ? FORMAT_HEIGHT
                            : (flyerPreviewRef.current?.scrollHeight || flyerHeight)
                          ) * previewScale,
                          position: 'relative',
                          minHeight: 40,
                          maxWidth: '100%',
                          animation: 'fadeIn 220ms ease-out',
                        }}
                      >
                        <div style={{
                          width: FORMAT_WIDTH,
                          transform: `scale(${previewScale})`,
                          transformOrigin: 'top left',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                        }}>
                          <div
                            ref={flyerPreviewRef}
                            dangerouslySetInnerHTML={{ __html: generatedHtml }}
                            style={{
                              width: FORMAT_WIDTH,
                              maxWidth: FORMAT_WIDTH,
                              ...(HAS_FIXED_HEIGHT ? { height: FORMAT_HEIGHT, maxHeight: FORMAT_HEIGHT } : {}),
                              background: '#fff',
                              overflow: 'hidden',
                            }}
                          />
                        </div>
                      </div>
                    );

                    const slotContent = !generatedHtml && !isGenerating
                      ? renderEmpty()
                      : (isGenerating || !hasMeasuredScale)
                      ? renderGenerating()
                      : renderScaled();

                    // ============ STORIES / REELS — full-bleed wireframe ============
                    if (format?.aspectRatio === '9/16') {
                      return (
                        <>
                          {/* Progress bars */}
                          <div className="absolute top-2.5 left-3 right-3 z-20 flex gap-1 pointer-events-none">
                            <div className="flex-1 h-0.5 bg-white/90 rounded-full" />
                            <div className="flex-1 h-0.5 bg-white/40 rounded-full" />
                            <div className="flex-1 h-0.5 bg-white/40 rounded-full" />
                          </div>
                          {/* User pill */}
                          <div className="absolute top-5 left-3 z-20 flex items-center gap-1.5 pointer-events-none">
                            <div className="w-5 h-5 rounded-full bg-white/90 ring-2 ring-white/60" />
                            <div className="h-1.5 w-12 rounded-full bg-white/80" />
                          </div>
                          {/* Action bar */}
                          <div className="absolute bottom-3 left-3 right-3 z-20 flex items-center gap-2 pointer-events-none">
                            <div className="flex-1 h-7 rounded-full bg-white/15 border border-white/35" />
                            <div className="w-5 h-5 rounded-full bg-white/25" />
                          </div>
                          {/* Content slot — fills the screen */}
                          <div
                            ref={previewContainerRef}
                            className="absolute inset-0 bg-slate-100 flex items-center justify-center overflow-hidden"
                          >
                            {slotContent}
                          </div>
                        </>
                      );
                    }

                    // ============ SQUARE POSTS — feed wireframe ============
                    if (format?.aspectRatio === '1/1') {
                      return (
                        <>
                          <PhoneStatusBar />
                          {/* Feed top bar */}
                          <div className="flex-shrink-0 flex items-center justify-between px-3 py-1.5 border-b border-slate-100">
                            <div className="h-2 w-12 rounded-full bg-slate-300" />
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-3 rounded-full border border-slate-300" />
                              <div className="w-3 h-3 rounded-full border border-slate-300" />
                            </div>
                          </div>
                          {/* Post header */}
                          <div className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-200 via-orange-100 to-amber-200 ring-1 ring-slate-200" />
                            <div className="flex-1">
                              <div className="h-1.5 w-16 rounded-full bg-slate-300" />
                              <div className="h-1 w-10 rounded-full bg-slate-200 mt-0.5" />
                            </div>
                            <div className="text-slate-300 text-[10px] leading-none">···</div>
                          </div>
                          {/* Content slot — square */}
                          <div
                            ref={previewContainerRef}
                            className="w-full bg-slate-100 flex items-center justify-center overflow-hidden"
                            style={{ aspectRatio: '1/1' }}
                          >
                            {slotContent}
                          </div>
                          {/* Action row */}
                          <div className="flex-shrink-0 flex items-center gap-2.5 px-3 py-1.5">
                            <div className="w-3.5 h-3.5 rounded-full border-[1.5px] border-slate-400" />
                            <div className="w-3.5 h-3.5 rounded-full border-[1.5px] border-slate-400" />
                            <div className="w-3.5 h-3.5 rounded-full border-[1.5px] border-slate-400" />
                          </div>
                          {/* Caption skeleton */}
                          <div className="flex-shrink-0 px-3 pb-1.5 space-y-0.5">
                            <div className="h-1 w-3/4 rounded-full bg-slate-200" />
                            <div className="h-1 w-1/2 rounded-full bg-slate-200" />
                          </div>
                        </>
                      );
                    }

                    // ============ FLYER (3:4) — Messages app wireframe ============
                    return (
                      <>
                        <PhoneStatusBar />
                        {/* Conversation header */}
                        <div className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 border-b border-slate-100">
                          <ArrowLeft className="w-3 h-3 text-blue-500 flex-shrink-0" />
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="h-1.5 w-20 rounded-full bg-slate-300" />
                            <div className="h-1 w-10 rounded-full bg-slate-200 mt-0.5" />
                          </div>
                        </div>
                        {/* Body */}
                        <div className="flex-1 flex flex-col p-3 gap-2 overflow-hidden">
                          {/* Incoming caption bubble */}
                          <div className="self-start max-w-[72%] bg-slate-100 rounded-2xl rounded-bl-sm px-2.5 py-1.5 space-y-0.5">
                            <div className="h-1.5 w-16 rounded-full bg-slate-300" />
                            <div className="h-1.5 w-20 rounded-full bg-slate-300" />
                          </div>
                          {/* Image attachment slot — 3:4 */}
                          <div
                            ref={previewContainerRef}
                            className="self-start bg-slate-100 rounded-2xl flex items-center justify-center overflow-hidden"
                            style={{ width: '72%', aspectRatio: '3/4' }}
                          >
                            {slotContent}
                          </div>
                        </div>
                        {/* Input bar */}
                        <div className="flex-shrink-0 px-3 pb-2.5 pt-1">
                          <div className="h-5 rounded-full bg-slate-100 border border-slate-200" />
                        </div>
                      </>
                    );
                  })()}
                </PhoneFrameSplit>
                )}

                {/* Floating download chip — top-right of phone, only after generation */}
                {generatedHtml && !isGenerating && (
                  <div className="absolute -top-2 -right-2 z-30">
                    <button
                      onClick={async () => {
                        try {
                          const canvas = await captureFlyer();
                          const link = document.createElement('a');
                          const safeId = (format?.id || 'design').replace(/[^a-z0-9_-]/gi, '');
                          link.download = `${safeId}_${Date.now()}.png`;
                          link.href = canvas.toDataURL('image/png');
                          link.click();
                        } catch (err) {
                          console.error('Download error:', err);
                          if (addNotification) addNotification('Failed to download', 'error');
                        }
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 text-[11px] font-semibold shadow-md transition-all"
                    >
                      <Download className="w-3 h-3" />
                      <span>PNG</span>
                    </button>
                  </div>
                )}
              </div>
                </ScaleToFit>
              </div>

              {/* Format caption — same shape/styling as picker */}
              {format && (
                <div className="mt-5 text-center flex-shrink-0">
                  <p className="text-sm font-bold text-slate-900">{format.label}</p>
                  {format.sublabel && (
                    <p className="text-[11px] text-slate-500 mt-0.5">{format.sublabel}</p>
                  )}
                  {format.width && format.height && (
                    <p className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[10px] text-slate-600 font-mono tabular-nums">
                      {format.width} × {format.height}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── PREVIEW STATE: generated flyer exists ──
  if (generatedHtml && !isGenerating) {
    return (
      <div className="space-y-3">
        <div ref={previewContainerRef} className={`${isHero ? 'bg-white border border-slate-200 rounded-3xl shadow-sm' : 'bg-white border border-slate-200 rounded-2xl shadow-sm'} overflow-hidden`}>
          <div className={`${isHero ? 'p-4' : 'p-3'} bg-gradient-to-br from-slate-50 to-slate-100 flex justify-center`}>
            <div style={{
              width: FLYER_WIDTH * previewScale,
              height: (flyerPreviewRef.current?.scrollHeight || flyerHeight) * previewScale,
              position: 'relative',
              minHeight: 100,
              maxWidth: '100%',
            }}>
              <div style={{
                width: FLYER_WIDTH,
                transform: `scale(${previewScale})`,
                transformOrigin: 'top left',
                position: 'absolute',
                top: 0,
                left: 0,
              }}>
                <div
                  ref={flyerPreviewRef}
                  dangerouslySetInnerHTML={{ __html: generatedHtml }}
                  style={{ width: FLYER_WIDTH, maxWidth: FLYER_WIDTH, background: '#fff', overflow: 'hidden' }}
                  className="shadow-2xl rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Edit / Regenerate input */}
        <div className="flex gap-1.5">
          <div className="relative flex-1">
            <Edit3 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && editNote.trim()) handleGenerate(); }}
              placeholder={isHero ? 'Tweak it — "make the headline bigger" or "swap to a navy palette"…' : 'e.g. Make text bigger, change to blue...'}
              className={`w-full pl-9 pr-3 py-3 rounded-xl bg-white border border-slate-200 ${isHero ? 'text-sm' : 'text-xs'} text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
            />
          </div>
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 text-xs font-semibold transition-all flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>{editNote.trim() ? 'Apply' : 'Redo'}</span>
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-red-50 border-red-200">
            <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
            <span className="text-xs font-medium text-red-700">{error}</span>
          </div>
        )}

        {/* Action row */}
        <div className="flex gap-2">
          <button
            onClick={async () => {
              try {
                const canvas = await captureFlyer();
                const link = document.createElement('a');
                link.download = `flyer_${Date.now()}.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
              } catch (err) {
                console.error('Download error:', err);
                if (addNotification) addNotification('Failed to download', 'error');
              }
            }}
            className={`px-4 py-3 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
              IS_SOCIAL
                // For social formats, download IS the primary action — promote
                // it to the prominent blue treatment and let it fill the row.
                ? 'flex-1 justify-center bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg'
                : 'text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300'
            }`}
          >
            <Download className="w-3.5 h-3.5" />
            <span>{IS_SOCIAL ? `Download for ${FORMAT_LABEL}` : 'PNG'}</span>
          </button>
          {/* Primary CTA (split variant):
              - Current Deal → "Save deal"
              - Other SMS-deliverable → "Use this flyer" (Send via SMS pipeline)
              - Social formats → hidden (download is the CTA) */}
          {IS_CURRENT_DEAL ? (
            <button
              onClick={handleSaveDeal}
              disabled={isSavingDeal || !generatedHtml}
              className={`flex-1 py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                dealSaved
                  ? 'bg-emerald-600 text-white shadow-md'
                  : isSavingDeal
                    ? 'bg-amber-400 text-white cursor-wait'
                    : 'bg-gradient-to-br from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-white shadow-md hover:shadow-lg'
              }`}
            >
              {isSavingDeal ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Saving…</span>
                </>
              ) : dealSaved ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>Saved · live in FAQ</span>
                </>
              ) : (
                <>
                  <Tag className="w-4 h-4" />
                  <span>Save deal</span>
                </>
              )}
            </button>
          ) : !IS_SOCIAL ? (
            <button
              onClick={handleApproveAndSend}
              disabled={isCapturing}
              className={`flex-1 py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                isCapturing
                  ? 'bg-blue-400 text-white cursor-wait'
                  : 'bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg'
              }`}
            >
              {isCapturing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Processing…</span>
                </>
              ) : (
                <>
                  <span>Use this flyer</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  // ── GENERATING STATE ──
  if (isGenerating) {
    return (
      <div className={`${isHero ? 'bg-gradient-to-br from-blue-50/40 via-white to-indigo-50/40 border border-blue-100 rounded-3xl shadow-sm' : 'bg-white border border-slate-200 rounded-2xl shadow-sm'} overflow-hidden flex flex-col items-center justify-center p-10 text-center min-h-[260px]`}>
        <div className="relative">
          <div className="absolute inset-0 rounded-2xl bg-blue-500/20 blur-xl animate-pulse" />
          <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-4 shadow-lg">
            <Sparkles className="w-7 h-7 text-white animate-pulse" />
          </div>
        </div>
        <h3 className="text-base font-bold text-slate-900 mb-1.5 mt-2">Designing your flyer…</h3>
        <p className="text-sm text-slate-500 max-w-[320px]">
          AI is crafting a professional design from your description. Usually takes 10–20 seconds.
        </p>
      </div>
    );
  }

  // ── INPUT STATE ──
  // Hero variant: no card chrome, larger prompt, premium feel.
  // Default variant: original card-chromed layout.

  if (isHero) {
    return (
      <div className="space-y-4">
        {/* Prompt — the moment */}
        <div className="relative group">
          <div className="absolute -inset-px bg-gradient-to-br from-blue-500/30 via-indigo-500/20 to-purple-500/30 rounded-3xl opacity-0 group-focus-within:opacity-100 blur-md transition-opacity duration-500" />
          <div className="relative bg-white border border-slate-200 group-focus-within:border-blue-300 rounded-3xl shadow-sm transition-colors overflow-hidden">
            <textarea
              value={description}
              onChange={(e) => { setDescription(e.target.value); setError(''); }}
              placeholder="Describe what you want — &ldquo;Memorial Day sale flyer, 40% off sofas, dark navy with gold accents, urgent feel&rdquo;"
              className="w-full h-28 px-5 pt-5 pb-3 bg-transparent text-[15px] text-slate-900 placeholder-slate-400 resize-none focus:outline-none leading-relaxed"
              maxLength={2000}
            />

            {/* Inline image strip + counter */}
            <div className="flex items-center justify-between gap-3 px-3 pb-3 pt-1 border-t border-slate-100">
              <div className="flex items-center gap-1.5 flex-wrap">
                {uploadedImages.map((img, i) => (
                  <div key={i} className="relative group/img w-9 h-9 rounded-lg overflow-hidden border border-slate-200 bg-white flex-shrink-0">
                    <img src={img.preview} alt={img.name} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeImage(i)}
                      className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity"
                      aria-label="Remove image"
                    >
                      <X className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                ))}
                {imageSlotsLeft > 0 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-9 h-9 rounded-lg border border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/40 flex items-center justify-center transition-all flex-shrink-0"
                    title={`Attach images (${uploadedImages.length}/${MAX_IMAGES})`}
                  >
                    <Plus className="w-4 h-4 text-slate-400" />
                  </button>
                )}
                <span className="text-[11px] text-slate-400 ml-1">
                  {uploadedImages.length > 0
                    ? `${uploadedImages.length}/${MAX_IMAGES} image${uploadedImages.length === 1 ? '' : 's'}`
                    : 'Attach product photos (optional)'}
                </span>
              </div>
              <span className="text-[11px] text-slate-400 flex-shrink-0">{description.length}/2000</span>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleImageUpload}
              multiple
              className="hidden"
            />
          </div>
        </div>

        {productPicker}

        {/* Quick ideas */}
        {!description && (
          <div>
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2 ml-1">Try one of these</p>
            <div className="flex flex-wrap gap-1.5">
              {promptSuggestions.map((prompt, i) => (
                <button
                  key={i}
                  onClick={() => setDescription(prompt)}
                  className="px-3 py-1.5 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-full text-xs text-slate-600 hover:text-blue-700 transition-all shadow-sm"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl border bg-red-50 border-red-200">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <span className="text-sm font-medium text-red-700">{error}</span>
          </div>
        )}

        {/* Generate */}
        <button
          onClick={handleGenerate}
          disabled={!canGenerate}
          className={`w-full py-4 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
            !canGenerate
              ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
              : 'bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-blue-600/30'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          <span>Generate with AI</span>
        </button>
      </div>
    );
  }

  // ── DEFAULT VARIANT (original card-chromed layout) ──
  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-4 pt-3 pb-3">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
              <Wand2 className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 leading-tight">AI Flyer Builder</h3>
              <p className="text-[11px] text-slate-500">Describe your flyer and AI will design it</p>
            </div>
          </div>

          <label className="block text-xs font-semibold text-slate-700 mb-1.5">
            <Sparkles className="w-3 h-3 inline mr-1 text-blue-500" />
            What do you want on the flyer?
          </label>
          <textarea
            value={description}
            onChange={(e) => { setDescription(e.target.value); setError(''); }}
            placeholder="e.g. Memorial Day sale — 40% off all living room furniture. Dark theme with gold accents."
            className="w-full h-[72px] p-2.5 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-900 resize-none focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all placeholder-slate-400"
            maxLength={2000}
          />
          <div className="flex items-end justify-end mt-1">
            <p className="text-[11px] text-slate-400">{description.length}/2000</p>
          </div>

          {!description && (
            <div className="mt-1">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Quick ideas</p>
              <div className="flex flex-wrap gap-1.5">
                {promptSuggestions.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => setDescription(prompt)}
                    className="px-2.5 py-1 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-lg text-[11px] text-slate-600 hover:text-blue-700 transition-all"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-slate-50/50 px-4 py-3">
          {productPicker}
        </div>

        <div className="border-t border-slate-200 bg-slate-50/50 px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Image className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-xs font-semibold text-slate-700">Product Images</span>
              <span className="text-[11px] text-slate-400">(optional — up to {MAX_IMAGES})</span>
            </div>
            {uploadedImages.length > 0 && (
              <span className="text-[11px] font-semibold text-blue-600">{uploadedImages.length}/{MAX_IMAGES}</span>
            )}
          </div>

          <div className="grid grid-cols-5 gap-1.5">
            {uploadedImages.map((img, i) => (
              <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 bg-white">
                <img src={img.preview} alt={img.name} className="w-full h-full object-cover" />
                <button
                  onClick={() => removeImage(i)}
                  className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity shadow-md"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}

            {imageSlotsLeft > 0 && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-blue-400 flex flex-col items-center justify-center cursor-pointer transition-all hover:bg-blue-50 gap-0.5"
              >
                {uploadedImages.length === 0 ? (
                  <>
                    <Upload className="w-4 h-4 text-slate-400" />
                    <span className="text-[9px] text-slate-500 font-medium">Upload</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 text-slate-400" />
                    <span className="text-[9px] text-slate-500 font-medium">Add</span>
                  </>
                )}
              </button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleImageUpload}
            multiple
            className="hidden"
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-red-50 border-red-200">
          <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
          <span className="text-xs font-medium text-red-700">{error}</span>
        </div>
      )}

      <button
        onClick={handleGenerate}
        disabled={!canGenerate}
        className={`w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
          !canGenerate
            ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'
        }`}
      >
        <Wand2 className="w-4 h-4" />
        <span>Generate Flyer</span>
      </button>

      {onCancel && (
        <button
          onClick={onCancel}
          className="w-full py-2 rounded-xl font-medium text-xs text-slate-500 hover:text-slate-700 hover:bg-white transition-all"
        >
          Cancel
        </button>
      )}
    </div>
  );
};

export default FlyerBuilder;